import React, { forwardRef } from 'react';
import T from 'prop-types';
import cx from 'classnames';

// @szhsin/react-menu's <Menu> only injects the live `isOpen` flag into a button it recognizes as
// its own MenuButton, which it does by reading a private marker the library sets via its internal
// `defineName`. We set the same marker locally instead of deep-importing from the package
// internals, so we don't couple to its dist layout.
// NOTE: the `_szhsinMenu` key and the `'MenuButton'` name are private to the lib re-verify both
// on a future major upgrade. See Menu.mjs.
const defineName = (name, component) =>
  Object.defineProperty(component, '_szhsinMenu', { value: name });

// eslint-disable-next-line prefer-arrow-callback
const MenuButton = forwardRef(function MenuButton(
  {
    size,
    shape,
    caret = true,
    isOpen,
    variant = 'transparent',
    children,
    disabled,
    frameless,
    className,
    ...restProps
  },
  ref
) {
  const expandedClassName =
    typeof className === 'function' ? className({ open: isOpen }) : className;

  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      className={cx('hms-dropdown-btn', expandedClassName, {
        'hms-dropdown-btn--open': isOpen,
        'hms-dropdown-btn--disabled': disabled,
        'hms-dropdown-btn--frameless': frameless,
        'hms-dropdown-btn--with-caret': caret,
        [`hms-dropdown-btn--${size}`]: size,
        [`hms-dropdown-btn--${shape}`]: shape,
        [`hms-dropdown-btn--${variant}`]: variant,
      })}
      {...restProps}
      aria-haspopup
      aria-expanded={isOpen}
      aria-disabled={disabled || undefined}
    >
      {children}
    </button>
  );
});

MenuButton.propTypes = {
  size: T.oneOf(['lg', 'sm']),
  variant: T.oneOf(['transparent', 'light', 'primary']),
  shape: T.oneOf(['square']),
  caret: T.bool,
  isOpen: T.bool,
  children: T.node,
  disabled: T.bool,
  frameless: T.bool,
  className: T.oneOfType([T.string, T.func]),
};

export default defineName('MenuButton', MenuButton);
